import { Button, CarouselNavigation, useStyles } from '@archipro-design/aria';
import { ArrowLeftSLine, ArrowRightSLine } from '@archipro-design/icons';
import * as S from './TagBarCarousel.style';
import type { FC } from 'react';
import { useState } from 'react';

interface TagBarProps {
    tags: { tag?: number; productCard?: JSX.Element }[];
    displayCount: number;
    className?: string;
}

export const THUMB_WIDTH = 204;

const getThumbGroupCount = (thumbs: number, displayCount: number) => {
    const thumbCountArray = Array.from(Array(thumbs).keys());

    let firstGroup = thumbs;

    if (thumbs > displayCount) {
        firstGroup = displayCount - 1;
    }

    return [
        thumbCountArray.splice(0, firstGroup),
        ...thumbCountArray.reduce((acc, _, __, arr) => {
            if (thumbCountArray.length > displayCount - 1) {
                acc.push(arr.splice(0, displayCount - 2));
            }
            return acc;
        }, [] as number[][]),
        thumbCountArray.splice(0, displayCount - 1),
    ];
};

const TagBarCarousel: FC<TagBarProps> = ({ tags, displayCount, className }) => {
    const [currentTabGroup, setCurrentTabGroup] = useState(1);

    const slideCount = tags.length;
    const thumbGroups = getThumbGroupCount(slideCount, displayCount);

    const totalTabGroups = thumbGroups.length;

    const showPrevBtn = currentTabGroup > 1;
    const showNextBtn =
        currentTabGroup !== totalTabGroups && slideCount > displayCount;

    const buttonsShowing = (showNextBtn ? 1 : 0) + (showPrevBtn ? 1 : 0);
    const currentlyDisplayed = displayCount - 2;

    const translateWidth =
        THUMB_WIDTH * currentlyDisplayed * (currentTabGroup - 1) * -1;

    const { css } = useStyles({
        slideCount,
        displayCount,
        showPrevBtn,
        showNextBtn,
        translateWidth,
        buttonsShowing,
    });

    const navigationItems = {
        'aria-label': 'Image tag gallery navigation',
        items: tags.map((item) => ({
            key: `tag-slide-${item.tag}`,
            'aria-controls': item.tag,
            content: item.productCard,
        })),
    };

    return (
        <div className={`${css(S.ThumbsContainer)} ${className}`}>
            <div className={css(S.LeftNavContainer)}>
                <Button
                    className={css(S.NavBtn)}
                    transparent
                    onClick={() => setCurrentTabGroup((prev) => prev - 1)}
                    icon={<ArrowLeftSLine />}
                />
            </div>
            <div className={css(S.ThumbList)}>
                {CarouselNavigation.create(navigationItems, {
                    generateKey: true,
                    defaultProps: () => ({
                        className: css(S.ThumbSlider),
                        maxTabs: displayCount,
                    }),
                })}
            </div>
            <div className={css(S.RightNavContainer)}>
                <Button
                    className={css(S.NavBtn)}
                    transparent
                    onClick={() => setCurrentTabGroup((prev) => prev + 1)}
                    icon={<ArrowRightSLine />}
                />
            </div>
        </div>
    );
};

export default TagBarCarousel;
